import React, { useState, useEffect, useRef } from "react";
import Cropper from "react-easy-crop";
import ReactCrop from "react-image-crop";
// @ts-ignore: no type declarations for CSS side-effect import
import "react-image-crop/dist/ReactCrop.css";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Checkbox } from "../components/ui/checkbox";
import { useToast } from "../hooks/useToast";
import { useNavigate } from "react-router-dom";
import { UserPlus, ArrowLeft, Settings, IndianRupee, Briefcase, Shield, Calendar } from "lucide-react";
import LeaveSettings from "./Settings/LeaveSettings";
import { createEmployee } from "../api/employees";
import { useAuth } from "../contexts/AuthContext";
import {
  createCompany, getCompany, uploadLogo, uploadSignature, getCompanyComplianceSettings,
  updateCompanyComplianceSettings
} from "../api/officeandcompany";
// The statutory figures below are FIXED BY LAW, not company settings: they are
// read straight from the module the payroll calc uses, so this screen can never
// drift from what is actually deducted. They are deliberately NOT part of
// `employeeData` and NOT posted — the backend has no column for them (they are
// absent from EmployeeTaxConfigSchema, so Pydantic drops them silently), and a
// company must not be able to override a statutory rate anyway.
import {
  pfWageCeiling,
  PF_EMPLOYER_RATE_PCT,
  ESIC_WAGE_CEILING,
  ESIC_EMPLOYEE_RATE_PCT,
  ESIC_EMPLOYER_RATE_PCT,
  STD_DEDUCTION_NEW,
  STD_DEDUCTION_OLD,
  REBATE_87A_NEW,
  REBATE_87A_OLD,
  REBATE_87A_LIMIT_NEW,
  REBATE_87A_LIMIT_OLD,
} from "../utils/employee.constants";

// ==================== STATUTORY DATA TABLES ====================

// Professional Tax — state-wise monthly slabs (FY 2025-26)
const PT_STATES: Record<string, { label: string; slabs: number[][]; annual: boolean; note?: string }> = {
  karnataka: { label: "Karnataka", slabs: [[0, 14999, 0], [15000, 99999999, 200]], annual: false },
  maharashtra: { label: "Maharashtra", slabs: [[0, 7500, 0], [7501, 10000, 175], [10001, 99999999, 200]], annual: false },
  westbengal: { label: "West Bengal", slabs: [[0, 8500, 0], [8501, 10000, 90], [10001, 15000, 110], [15001, 25000, 130], [25001, 40000, 150], [40001, 99999999, 200]], annual: false },
  telangana: { label: "Telangana", slabs: [[0, 14999, 0], [15000, 20000, 150], [20001, 99999999, 200]], annual: false },
  tamilnadu: { label: "Tamil Nadu", slabs: [[0, 3500, 0], [3501, 5000, 22], [5001, 7500, 33], [7501, 10000, 52], [10001, 12500, 63], [12501, 15000, 83], [15001, 20000, 104], [20001, 25000, 135], [25001, 99999999, 182]], annual: false },
  gujarat: { label: "Gujarat", slabs: [[0, 5999, 0], [6000, 8999, 80], [9000, 11999, 150], [12000, 99999999, 200]], annual: false },
  kerala: { label: "Kerala", slabs: [[0, 99999999, 208]], annual: false },
  andhra: { label: "Andhra Pradesh", slabs: [[0, 14999, 0], [15000, 20000, 150], [20001, 99999999, 200]], annual: false },
  mp: { label: "Madhya Pradesh", slabs: [[0, 18749, 0], [18750, 25000, 125], [25001, 33333, 167], [33334, 99999999, 208]], annual: false },
  assam: { label: "Assam", slabs: [[0, 9999, 0], [10000, 14999, 150], [15000, 24999, 180], [25000, 99999999, 208]], annual: false },
  bihar: { label: "Bihar", slabs: [[0, 99999999, 0]], annual: false, note: "No PT in Bihar" },
  delhi: { label: "Delhi", slabs: [[0, 99999999, 0]], annual: false, note: "No PT in Delhi" },
  rajasthan: { label: "Rajasthan", slabs: [[0, 99999999, 0]], annual: false, note: "No PT in Rajasthan" },
  none: { label: "Not Applicable", slabs: [[0, 99999999, 0]], annual: false },
};

// Labour Welfare Fund — state-wise
const LWF_STATES: Record<string, { label: string; ee: number; er: number; freq: string }> = {
  andhra: { label: "Andhra Pradesh", ee: 20, er: 40, freq: "annual" },
  delhi: { label: "Delhi", ee: 0.75, er: 2.25, freq: "monthly" },
  gujarat: { label: "Gujarat", ee: 6, er: 12, freq: "halfyearly" },
  haryana: { label: "Haryana", ee: 25, er: 50, freq: "monthly" },
  karnataka: { label: "Karnataka", ee: 20, er: 40, freq: "annual" },
  kerala: { label: "Kerala", ee: 4, er: 8, freq: "halfyearly" },
  madhyapradesh: { label: "Madhya Pradesh", ee: 10, er: 20, freq: "halfyearly" },
  maharashtra: { label: "Maharashtra", ee: 25, er: 75, freq: "halfyearly" },
  odisha: { label: "Odisha", ee: 10, er: 20, freq: "annual" },
  punjab: { label: "Punjab", ee: 10, er: 20, freq: "monthly" },
  rajasthan: { label: "Rajasthan", ee: 25, er: 50, freq: "monthly" },
  tamilnadu: { label: "Tamil Nadu", ee: 10, er: 20, freq: "halfyearly" },
  telangana: { label: "Telangana", ee: 20, er: 40, freq: "annual" },
  westbengal: { label: "West Bengal", ee: 3, er: 15, freq: "monthly" },
  none: { label: "Not Applicable", ee: 0, er: 0, freq: "monthly" },
};

// Helper functions
function calcPT(grossMonthly: number, stateKey: string): number {
  const cfg = PT_STATES[stateKey] || PT_STATES.none;
  for (const [lo, hi, amt] of cfg.slabs) {
    if (grossMonthly >= lo && grossMonthly <= hi) return amt;
  }
  return 0;
}

function lwfMonthly(stateKey: string): { ee: number; er: number } {
  const s = LWF_STATES[stateKey] || LWF_STATES.none;
  if (s.freq === "monthly") return { ee: s.ee, er: s.er };
  if (s.freq === "halfyearly") return { ee: +(s.ee / 6).toFixed(2), er: +(s.er / 6).toFixed(2) };
  if (s.freq === "annual") return { ee: +(s.ee / 12).toFixed(2), er: +(s.er / 12).toFixed(2) };
  return { ee: 0, er: 0 };
}

function fmt(n: number | string): string {
  const num = typeof n === 'string' ? parseFloat(n) : n;
  if (isNaN(num)) return "0.00";
  return new Intl.NumberFormat("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(num || 0);
}

export const BASE_URL = process.env.REACT_APP_API_BASE_URL;

const AddCompany = () => {
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<"basic" | "compliance" | "leaves">("basic");
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [signaturePreview, setSignaturePreview] = useState<string | null>(null);
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({
    unit: "px",
    width: 50,
    height: 50,
    x: 25,
    y: 25,
  });

  const [completedCrop, setCompletedCrop] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [showCropModal, setShowCropModal] = useState(false);
  const [signatureImageSrc, setSignatureImageSrc] = useState(null);
  const [showSignatureCropModal, setShowSignatureCropModal] = useState(false);
  const [signatureCrop, setSignatureCrop] = useState({
    unit: "%",
    width: 60,
    height: 30,
    x: 20,
    y: 30,
  });

  const [completedSignatureCrop, setCompletedSignatureCrop] = useState(null);
  const [signatureZoom, setSignatureZoom] = useState(1);
  const [signatureRotation, setSignatureRotation] = useState(0);
  const [employeeData, setEmployeeData] = useState({
    companyName: "",
    email: "",
    mobile: 0,
    organization: "",
    nature: "",
    gstin: "",
    panNumber: "",
    address_1: "",
    address_2: "",
    pincode: 0,
    city: "",
    state: "",
    country: "",
    signaturePath: "",
    logoPath: "",
    saveinfo: false,
    // Compliance Settings
    pfApplicable: true,
    esicApplicable: true,
    ptApplicable: true,
    lwfApplicable: true,
    tdsApplicable: true,
    pfBasis: "statutory" as "statutory" | "actual",
    pfEmployeeRate: 12,
    // When true, the employer's 12% PF is treated as part of CTC (so it
    // reduces take-home). When false it's paid over-and-above CTC and
    // doesn't reduce the employee's salary. The employee's own PF is
    // always deducted from salary in either case.
    pfFromCtc: true,
    ptState: "karnataka",
    lwfState: "karnataka",
    defaultTaxRegime: "new" as "new" | "old",
  });

  const { toast } = useToast();
  const { user } = useAuth();
  const navigate = useNavigate();

  const organization = [
    { id: "1", label: "Proprietorship" },
    { id: "2", label: "Partnership" },
    { id: "3", label: "Limited Liability Partnership" },
    { id: "4", label: "Private Limited Companies" },
    { id: "5", label: "Public Limited Companies" },
    { id: "6", label: "One - Person Companies" },
    { id: "7", label: "Hindu Undivided Family(HUF)" },
    { id: "8", label: "Local Authority" },
    { id: "9", label: "Foreign Company" },
    { id: "10", label: "Society / Club / AOP / NGO" },
    { id: "11", label: "Statuary Body" },
    { id: "12", label: "Others" },
  ];

  const nature = [
    "Manufacturer",
    "Traders",
    "Services Providers",
    "Shops",
    "Restaurant/Foods Related Services",
    "Importer/Exporter",
    "Others",
  ];


  const fetchCompany = async () => {
    try {
      setLoading(true);
      console.log("Fetching company...");
      const response = await getCompany();
      console.log("Company fetched successfully:", response.companies?.length || 0);

      // Fetch compliance settings separately
      const complianceResponse = await getCompanyComplianceSettings();
      const complianceArray = complianceResponse.data || complianceResponse;
      const complianceData = Array.isArray(complianceArray)
        ? complianceArray[0]
        : complianceArray;

      if (response.companies && response.companies.length > 0) {
        const company = response.companies[0];
        setEmployeeData({
          ...employeeData,
          companyName: company.companyName || "",
          email: company.email || "",
          mobile: company.mobile || 0,
          organization: company.organization || "",
          nature: company.nature || "",
          gstin: company.gstin || "",
          panNumber: company.panNumber || "",
          address_1: company.address_1 || "",
          address_2: company.address_2 || "",
          pincode: company.pincode || 0,
          city: company.city || "",
          state: company.state || "",
          country: company.country || "",
          signaturePath: company.signaturePath || "",
          logoPath: company.logoPath || "",
          saveinfo: company.saveinfo === true,
          // Compliance settings from tax config API
          pfApplicable: complianceData?.pfApplicable === true,
          esicApplicable: complianceData?.esicApplicable === true,
          ptApplicable: complianceData?.ptApplicable === true,
          lwfApplicable: complianceData?.lwfApplicable === true,
          tdsApplicable: complianceData?.tdsApplicable === true,
          pfBasis: complianceData?.pfBasis || "statutory",
          pfEmployeeRate: complianceData?.pfEmployeeRate || 12,
          pfFromCtc: complianceData?.pfFromCtc !== false,
          ptState: complianceData?.ptState || "karnataka",
          lwfState: complianceData?.lwfState || "karnataka",
          defaultTaxRegime: complianceData?.defaultTaxRegime || "new",
        });

        // Prefer the public GCS URL the backend now returns. Fall back to the
        // legacy `${BASE_URL}/${path}` form only for very old rows where the
        // backend hasn't been redeployed yet.
        if (company.logoUrl) {
          setLogoPreview(company.logoUrl);
        } else if (company.logoPath) {
          setLogoPreview(`${BASE_URL}/${company.logoPath}`);
        }

        if (company.signatureUrl) {
          setSignaturePreview(company.signatureUrl);
        } else if (company.signaturePath) {
          setSignaturePreview(`${BASE_URL}/${company.signaturePath}`);
        }
      }

      // if (employeeData.logoPath) {
      //   setLogoPreview(`${BASE_URL}/${employeeData.logoPath}`);
      // }

      // if (employeeData.signaturePath) {
      //   setSignaturePreview(`${BASE_URL}/${employeeData.signaturePath}`);
      // }
    } catch (error: any) {
      console.error("Error fetching company:", error);
      // Don't show error if it's just no data found
      if (error.message !== "No company found") {
        toast({
          // title: "Error",
          description: error.message || "Failed to load company",
          variant: "destructive",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompany();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setLoading(true);

      let logoPath = employeeData.logoPath || "";
      let signaturePath = employeeData.signaturePath || "";

      // 1️⃣ Upload Logo if new file selected
      if (employeeData.logoPath instanceof File) {
        const logoRes = await uploadLogo(employeeData.logoPath);
        // New shape: { success, message, data: { logoPath, logoUrl } }
        // Legacy shape (pre-GCS): { path: "files/..." }
        logoPath = logoRes?.data?.logoPath ?? logoRes?.path ?? "";
        const newLogoUrl =
          logoRes?.data?.logoUrl ??
          (logoRes?.path ? `${BASE_URL}/${logoRes.path}` : "");
        if (newLogoUrl) setLogoPreview(newLogoUrl);
      }

      // 2️⃣ Upload Signature if new file selected
      if (employeeData.signaturePath instanceof File) {
        const signRes = await uploadSignature(employeeData.signaturePath);
        signaturePath = signRes?.data?.signaturePath ?? signRes?.path ?? "";
        const newSignatureUrl =
          signRes?.data?.signatureUrl ??
          (signRes?.path ? `${BASE_URL}/${signRes.path}` : "");
        if (newSignatureUrl) setSignaturePreview(newSignatureUrl);
      }

      // 3️⃣ Prepare Clean Payload
      const payload = {
        ...employeeData,
        logoPath,
        signaturePath,
      };

      // Remove file objects
      delete (payload as any).upload_logo;
      delete (payload as any).upload_signature;

      const response = await createCompany(payload);

      if (response.success) {
        fetchCompany();
        toast({
          title: "Success",
          description: "Company updated successfully",
        });
        // Advance to Compliance & Salary Settings, land at the top.
        setActiveTab("compliance");
        scrollToTopOfPage();
      }
    } catch (error: any) {
      toast({
        variant: "destructive",
        // title: "Error",
        description: error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleComplianceSubmit = async () => {
    try {
      setLoading(true);
      const payload = {
        pfApplicable: employeeData.pfApplicable,
        esicApplicable: employeeData.esicApplicable,
        ptApplicable: employeeData.ptApplicable,
        lwfApplicable: employeeData.lwfApplicable,
        tdsApplicable: employeeData.tdsApplicable,
        pfBasis: employeeData.pfBasis,
        pfEmployeeRate: employeeData.pfEmployeeRate,
        pfFromCtc: employeeData.pfFromCtc,
        ptState: employeeData.ptState,
        lwfState: employeeData.lwfState,
        taxRegime: employeeData.defaultTaxRegime,
      };

      const response = await updateCompanyComplianceSettings(payload);
      // The backend endpoint `/employee-tax-config` returns `{ message, data }`
      // (no `success` flag), so treat any non-throw as success. We just check
      // that we actually got something back rather than insisting on `.success`.
      const ok = !!(response && (response.success ?? response.data ?? response.message));
      if (ok) {
        toast({
          title: "Success",
          description: response?.message || "Compliance settings updated successfully",
        });
        // Refresh to get updated data
        await fetchCompany();
        // Advance to Leaves Settings, land at the top.
        setActiveTab("leaves");
        scrollToTopOfPage();
      }
    } catch (error: any) {
      toast({
        variant: "destructive",
        // title:"Error",
        description: error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    console.log(`AddCompany: ${field} field changed to:`, value);
    setEmployeeData((prev) => {
      const newData = {
        ...prev,
        [field]: value,
      };
      return newData;
    });
  };
  const handleCrop = async () => {
    const canvas = document.getElementById("previewCanvas");

    canvas.toBlob((blob) => {
      const file = new File([blob], "logo.jpg", {
        type: "image/jpeg",
      });

      handleInputChange("logoPath", file);
      setLogoPreview(URL.createObjectURL(file));
      setShowCropModal(false);
    });
  };


  const country = [
    { id: 1, label: "Unitied Kingdom" },
    { id: 2, label: "Argentina" },
    { id: 3, label: "Australia" },
    { id: 4, label: "Bahamas" },
    { id: 5, label: "Belgium" },
    { id: 6, label: "Brazil" },
    { id: 7, label: "Canada" },
    { id: 8, label: "China" },
    { id: 9, label: "Colombia" },
    { id: 10, label: "cuba" },
    { id: 11, label: "Dominican Republic" },
    { id: 12, label: "Ecuador" },
    { id: 13, label: "El salvador" },
    { id: 14, label: "France" },
    { id: 15, label: "Germany" },
    { id: 16, label: "Guatemala" },
    { id: 17, label: "Haiti" },
    { id: 18, label: "Honduras" },
    { id: 19, label: "India" },
    { id: 20, label: "Ireland" },
    { id: 21, label: "Israel" },
    { id: 22, label: "Italy" },
    { id: 23, label: "Japan" },
    { id: 24, label: "Korea" },
    { id: 25, label: "Mexico" },
    { id: 26, label: "Netherlands" },
    { id: 27, label: "Philippines" },
    { id: 28, label: "Spain" },
    { id: 29, label: "Sweden" },
    { id: 30, label: "Switzerland" },
    { id: 31, label: "Taiwan" },
    { id: 32, label: "Venezuela" },
    { id: 33, label: "Vietnam" },
    { id: 34, label: "Afghanistan" },
    { id: 35, label: "Albania" },
    { id: 36, label: "Algeria" },
    { id: 37, label: "American samoa" },
    { id: 38, label: "Andorra" },
    { id: 39, label: "Angola" },
    { id: 40, label: "Armenia" },
    { id: 41, label: "Austria" },
    { id: 42, label: "Azerbaijan" },
    { id: 43, label: "Bahrain" },
    { id: 44, label: "Bangladesh" },
    { id: 45, label: "Barbados" },
    { id: 46, label: "Belarus" },
    { id: 47, label: "Belize" },
    { id: 48, label: "Benin" },
    { id: 49, label: "Bermuda" },
    { id: 50, label: "Bhutan" },
    { id: 51, label: "Bolivia" },
    { id: 52, label: "Bosnia And Herzegovina" },
    { id: 53, label: "Botswana" },
    { id: 54, label: "Bulgaria" },
    { id: 55, label: "Burkina Faso" },
    { id: 56, label: "Burundi" },
    { id: 57, label: "Cambodia" },
    { id: 58, label: "Cameroon" },
    { id: 59, label: "Cape Verde Islands" },
    { id: 60, label: "Chad" },
    { id: 61, label: "Chile" },
    { id: 62, label: "Congo" },
    { id: 63, label: "Costa Rica" },
    { id: 64, label: "Croatia" },
    { id: 65, label: "Cyprus" },
    { id: 66, label: "Czech Republic" },
    { id: 67, label: "Denmark" },
    { id: 68, label: "Djibouti" },
    { id: 69, label: "Dominica" },
    { id: 70, label: "Egypt" },
    { id: 71, label: "Eritrea" },
    { id: 72, label: "Estonia" },
    { id: 73, label: "Ethiopia" },
    { id: 74, label: "Fiji" },
    { id: 75, label: "Finland" },
    { id: 76, label: "French Polynesia" },
    { id: 77, label: "Gabon" },
    { id: 78, label: "Gambia" },
    { id: 79, label: "Georgia" },
    { id: 80, label: "Ghana" },
    { id: 81, label: "Greece" },
    { id: 82, label: "Grenada" },
    { id: 83, label: "Guinea" },
    { id: 84, label: "Guyana" },
    { id: 85, label: "Hungary" },
    { id: 86, label: "Iceland" },
    { id: 87, label: "Indonesia" },
    { id: 88, label: "Iran" },
    { id: 89, label: "Iraq" },
    { id: 90, label: "Jamaica" },
    { id: 91, label: "Jordan" },
    { id: 92, label: "Kazakhstan" },
    { id: 93, label: "Kenya" },
    { id: 94, label: "Kuwait" },
    { id: 95, label: "Latvia" },
    { id: 96, label: "Lebanon" },
    { id: 97, label: "Liberia" },
    { id: 98, label: "Libya" },
    { id: 99, label: "Lithuania" },
    { id: 100, label: "LUXEMBOURG Luxembourg" },
    { id: 101, label: "Madagascar" },
    { id: 102, label: "Malawi" },
    { id: 103, label: "Malaysia" },
    { id: 104, label: "Maldives" },
    { id: 105, label: "Mali" },
    { id: 106, label: "Malta" },
    { id: 107, label: "Mauritania" },
    { id: 108, label: "Mauritius" },
    { id: 109, label: "Monaco" },
    { id: 110, label: "Mongolia" },
    { id: 111, label: "Montenegro" },
    { id: 112, label: "Morocco" },
    { id: 113, label: "Mozambique" },
    { id: 114, label: "Namibia" },
    { id: 115, label: "Nepal" },
    { id: 116, label: "New Zealand" },
    { id: 117, label: "Nicaragua" },
    { id: 118, label: "Niger" },
    { id: 119, label: "Nigeria" },
    { id: 120, label: "Norway" },
    { id: 121, label: "Oman" },
    { id: 122, label: "Pakistan" },
    { id: 123, label: "Panama" },
    { id: 124, label: "Papua New Guinea" },
    { id: 125, label: "Paraguay" },
    { id: 126, label: "Peru" },
    { id: 127, label: "Poland" },
    { id: 128, label: "Portugal" },
    { id: 129, label: "Qatar" },
    { id: 130, label: "Romania" },
    { id: 131, label: "Rwanda" },
    { id: 132, label: "Saudi Arabia" },
    { id: 133, label: "Senegal" },
    { id: 134, label: "Serbia" },
    { id: 135, label: "Sierrarra Leone" },
    { id: 136, label: "Singapore" },
    { id: 137, label: "Slovakia" },
    { id: 138, label: "Slovenia" },
    { id: 139, label: "Solomon Islands" },
    { id: 140, label: "Somalia" },
    { id: 141, label: "South Africa" },
    { id: 142, label: "Sri Lanka" },
    { id: 143, label: "Sudan" },
    { id: 144, label: "Suriname" },
    { id: 145, label: "Swaziland" },
    { id: 146, label: "Tajikistan" },
    { id: 147, label: "Thailand" },
    { id: 148, label: "Togo" },
    { id: 149, label: "Trinidad And Tobago" },
    { id: 150, label: "Tunisia" },
    { id: 151, label: "Turkey" },
    { id: 152, label: "Turkmenistan" },
    { id: 153, label: "Tuvalu" },
    { id: 154, label: "Uganda" },
    { id: 155, label: "Ukraine" },
    { id: 156, label: "United Arab Emirates" },
    { id: 157, label: "United States" },
    { id: 158, label: "Uruguay" },
    { id: 159, label: "Uzbekistan" },
    { id: 160, label: "Vanuatu" },
    { id: 161, label: "Yemen" },
    { id: 162, label: "Zambia" },
    { id: 163, label: "Tanzania" },
    { id: 164, label: "Other" },
    { id: 165, label: "Russia" },
  ];

  const state = [
    { id: 1, label: "01 Jammu & Kashmir" },
    { id: 2, label: "02 Himachal Pradesh" },
    { id: 3, label: "03 Punjab" },
    { id: 4, label: "04 Chandigarh" },
    { id: 5, label: "05 Uttarakhand" },
    { id: 6, label: "06 Haryana" },
    { id: 7, label: "07 Delhi" },
    { id: 8, label: "08 Rajasthan" },
    { id: 9, label: "09 Uttar Pradesh" },
    { id: 10, label: "10 Bihar" },
    { id: 11, label: "11 Sikkim" },
    { id: 12, label: "12 Arunachal Pradesh" },
    { id: 13, label: "13 Nagaland" },
    { id: 14, label: "14 Manipur" },
    { id: 15, label: "15 Mizoram" },
    { id: 16, label: "16 Tripura" },
    { id: 17, label: "17 Meghalaya" },
    { id: 18, label: "18 Assam" },
    { id: 19, label: "19 West Bengal" },
    { id: 20, label: "20 Jharkhand" },
    { id: 21, label: "21 Odisha" },
    { id: 22, label: "22 Chattisgarh" },
    { id: 23, label: "23 Madhya Pradesh" },
    { id: 24, label: "24 Gujarat" },
    { id: 25, label: "25 Daman & Diu" },
    { id: 26, label: "26 Dadra & Nagar Haveli" },
    { id: 27, label: "27 Maharashtra" },
    { id: 28, label: "28 Old Andhra Pradesh" },
    { id: 29, label: "29 Karnataka" },
    { id: 30, label: "30 Goa" },
    { id: 31, label: "31 Lakshadweep" },
    { id: 32, label: "32 Kerala" },
    { id: 33, label: "33 Tamil Nadu" },
    { id: 34, label: "34 Puducherry" },
    { id: 35, label: "35 Andaman & Nicobar" },
    { id: 36, label: "36 Telangana" },
    { id: 37, label: "37 New Andhra Pradesh" },
    { id: 38, label: "Other" },
  ];


  const ptCfg = PT_STATES[employeeData.ptState] || PT_STATES.none;
  const lwfCfg = LWF_STATES[employeeData.lwfState] || LWF_STATES.none;


  const handleSignatureCrop = () => {
    const canvas = document.getElementById("signaturePreviewCanvas") as HTMLCanvasElement;

    canvas.toBlob((blob) => {
      const file = new File([blob], "signature.jpg", {
        type: "image/jpeg",
      });

      handleInputChange("signaturePath", file);
      setSignaturePreview(URL.createObjectURL(file));
      setShowSignatureCropModal(false);
    });
  };

  useEffect(() => {
    if (!completedCrop || !imgRef.current) return;

    const image = imgRef.current;
    const canvas = document.getElementById("previewCanvas");
    const ctx = canvas.getContext("2d");

    // 🔥 CORRECT scaling (THIS FIXES YOUR ISSUE)
    const scaleX = image.naturalWidth / image.clientWidth;
    const scaleY = image.naturalHeight / image.clientHeight;

    const pixelCrop = {
      x: completedCrop.x * scaleX,
      y: completedCrop.y * scaleY,
      width: completedCrop.width * scaleX,
      height: completedCrop.height * scaleY,
    };

    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;

    ctx.drawImage(
      image,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      pixelCrop.width,
      pixelCrop.height
    );
  }, [completedCrop]);

  useEffect(() => {
    if (!completedSignatureCrop || !signatureImgRef.current) return;

    const image = signatureImgRef.current;
    const canvas = document.getElementById("signaturePreviewCanvas") as HTMLCanvasElement;
    const ctx = canvas.getContext("2d");

    // 🔥 SAME FIX AS LOGO
    const scaleX = image.naturalWidth / image.clientWidth;
    const scaleY = image.naturalHeight / image.clientHeight;

    const pixelCrop = {
      x: completedSignatureCrop.x * scaleX,
      y: completedSignatureCrop.y * scaleY,
      width: completedSignatureCrop.width * scaleX,
      height: completedSignatureCrop.height * scaleY,
    };

    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;

    ctx.drawImage(
      image,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      pixelCrop.width,
      pixelCrop.height
    );
  }, [completedSignatureCrop]);

  const imgRef = useRef(null);
  const signatureImgRef = useRef<HTMLImageElement | null>(null);
  // Anchor for tab transitions — used to find the nearest scrollable ancestor
  // so the next tab always lands at the top of its content.
  const pageRef = useRef<HTMLDivElement | null>(null);

  const scrollToTopOfPage = () => {
    const doScroll = () => {
      try {
        let el: HTMLElement | null = pageRef.current;
        while (el) {
          const style = window.getComputedStyle(el);
          const oy = style.overflowY;
          const scrollable =
            (oy === "auto" || oy === "scroll" || oy === "overlay") &&
            el.scrollHeight > el.clientHeight;
          if (scrollable) el.scrollTop = 0;
          el = el.parentElement;
        }
      } catch {}
      if (typeof window !== "undefined") {
        window.scrollTo({ top: 0, behavior: "auto" });
      }
      if (typeof document !== "undefined") {
        if (document.documentElement) document.documentElement.scrollTop = 0;
        if (document.body) document.body.scrollTop = 0;
      }
    };
    doScroll();
    if (typeof requestAnimationFrame !== "undefined") {
      requestAnimationFrame(doScroll);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div ref={pageRef} className="w-full overflow-visible">
      <Card className="w-full max-w-full">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 text-2xl">
                <UserPlus className="h-6 w-6" />
                Company Settings
              </CardTitle>
              <CardDescription>
                Configure your company details and compliance settings
              </CardDescription>
            </div>
          </div>
        </CardHeader>

        {/* Tab Navigation */}
        <div className="px-6 border-b border-gray-200">
          <Tabs
            value={activeTab}
            onValueChange={(v) => { setActiveTab(v as any); scrollToTopOfPage(); }}
            className="w-full"
          >
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="basic" className="flex items-center gap-2">
                <UserPlus className="h-4 w-4" />
                Basic Information
              </TabsTrigger>
              <TabsTrigger value="compliance" className="flex items-center gap-2">
                <Shield className="h-4 w-4" />
                Compliance & Salary Settings
              </TabsTrigger>
              <TabsTrigger value="leaves" className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Leaves Settings
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        <CardContent className="p-6 ">
          {/* Basic Information Tab */}
          {activeTab === "basic" && (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="name">Company Name *</Label>
                  <Input
                    id="name"
                    value={employeeData.companyName}
                    onChange={(e) => handleInputChange("companyName", e.target.value)}
                    placeholder="Enter company name"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={employeeData.email}
                    onChange={(e) => handleInputChange("email", e.target.value)}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="mobile">Phone</Label>
                  <Input
                    id="mobile"
                    type="number"
                    onWheel={(e) => e.currentTarget.blur()}
                    value={employeeData.mobile}
                    onChange={(e) => handleInputChange("mobile", e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="organization">Organization Type *</Label>
                  <Select
                    value={employeeData.organization}
                    onValueChange={(value) => handleInputChange("organization", value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select organization" />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      {organization.map((e) => (
                        <SelectItem key={e.id} value={e.label}>{e.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="nature">Nature Of Business *</Label>
                  <Select
                    value={employeeData.nature}
                    onValueChange={(value) => handleInputChange("nature", value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select nature" />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      {nature.map((e) => (
                        <SelectItem key={e} value={e}>{e}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="gstin">GST Number *</Label>
                  <Input
                    id="gstin"
                    value={employeeData.gstin}
                    onChange={(e) => handleInputChange("gstin", e.target.value)}
                    placeholder="Enter GST number"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="panNumber">Pan Number *</Label>
                  <Input
                    id="panNumber"
                    value={employeeData.panNumber}
                    onChange={(e) => handleInputChange("panNumber", e.target.value)}
                    placeholder="Enter pan number"
                    required
                  />
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="address_1">Address Line 1 *</Label>
                  <Input
                    id="address_1"
                    value={employeeData.address_1}
                    onChange={(e) => handleInputChange("address_1", e.target.value)}
                    placeholder="Enter Address Line 1"
                    required
                  />
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="address_2">Address Line 2</Label>
                  <Input
                    id="address_2"
                    value={employeeData.address_2}
                    onChange={(e) => handleInputChange("address_2", e.target.value)}
                    placeholder="Enter Address Line 2"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="city">City *</Label>
                  <Input
                    id="city"
                    value={employeeData.city}
                    onChange={(e) => handleInputChange("city", e.target.value)}
                    placeholder="Enter city"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="pincode">Pincode *</Label>
                  <Input
                    id="pincode"
                    value={employeeData.pincode}
                    type="number"
                    onWheel={(e) => e.currentTarget.blur()}
                    onChange={(e) => handleInputChange("pincode", e.target.value)}
                    placeholder="Enter pincode"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="state">State *</Label>
                  <Select
                    value={employeeData.state}
                    onValueChange={(value) => handleInputChange("state", value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select state" />
                    </SelectTrigger>
                    <SelectContent className="bg-white max-h-60">
                      {state.map((e) => (
                        <SelectItem key={e.id} value={e.label}>{e.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="country">Country *</Label>
                  <Select
                    value={employeeData.country}
                    onValueChange={(value) => handleInputChange("country", value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select country" />
                    </SelectTrigger>
                    <SelectContent className="bg-white max-h-60">
                      {country.map((e) => (
                        <SelectItem key={e.id} value={e.label}>{e.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* <div className="space-y-2">
                  <Label htmlFor="upload_logo">Upload Logo</Label>
                  <Input
                    id="logoPath"
                    type="file"
                    accept="image/jpeg,image/png,image/jpg"

                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;

                      const allowedTypes = ["image/jpeg", "image/png", "image/jpg"];

                      if (!allowedTypes.includes(file.type)) {
                        toast({ description: "Only JPG, JPEG, PNG files are allowed", variant: "destructive" });
                        return;
                      }

                      const reader = new FileReader();
                      reader.onload = () => {
                        setImageSrc(reader.result);
                        setShowCropModal(true); // 🔥 OPEN MODAL
                      };
                      reader.readAsDataURL(file);
                    }}
                  />
                  <p className="text-xs text-gray-500">
                    Allowed formats: JPG, JPEG, PNG
                  </p>
                  {(logoPreview || employeeData.logoPath) && (
                    <img
                      src={logoPreview || ""}
                      alt="Logo Preview"
                      className="h-24 w-24 object-contain border rounded-md mt-2"
                    />
                  )}
                </div> */}
                <div className="space-y-2">
                  <Label htmlFor="upload_logo">Upload Logo</Label>

                  <div className="relative group">
                    <Input
                      id="logoPath"
                      type="file"
                      accept="image/jpeg,image/png,image/jpg"
                      disabled={!employeeData.saveinfo}
                      className={!employeeData.saveinfo ? "cursor-not-allowed opacity-60" : ""}
                      onChange={(e) => {
                        if (!employeeData.saveinfo) return;

                        const file = e.target.files?.[0];
                        if (!file) return;

                        const allowedTypes = ["image/jpeg", "image/png", "image/jpg"];

                        if (!allowedTypes.includes(file.type)) {
                          toast({ description: "Only JPG, JPEG, PNG files are allowed", variant: "destructive" });
                          e.target.value = "";
                          return;
                        }

                        // Client-side size guard — backend enforces the same 2 MB cap.
                        const LOGO_MAX_BYTES = 2 * 1024 * 1024;
                        if (file.size > LOGO_MAX_BYTES) {
                          toast({ description: "Logo must be 2 MB or smaller.", variant: "destructive" });
                          e.target.value = "";
                          return;
                        }

                        const reader = new FileReader();
                        reader.onload = () => {
                          setImageSrc(reader.result);
                          setShowCropModal(true);
                        };

                        reader.readAsDataURL(file);
                      }}
                    />

                    {!employeeData.saveinfo && (
                      <>
                        <div className="absolute inset-0 z-10 cursor-not-allowed" />

                        <div className="absolute hidden group-hover:flex -top-10 left-0 bg-black text-white text-xs px-3 py-2 rounded-md shadow-lg z-20 whitespace-nowrap">
                          First save company information
                        </div>
                      </>
                    )}
                  </div>

                  <p className="text-xs text-gray-500">
                    Allowed formats: JPG, JPEG, PNG · Max size: 2 MB
                  </p>

                  {(logoPreview || employeeData.logoPath) && (
                    <img
                      src={logoPreview || ""}
                      alt="Logo Preview"
                      className="h-24 w-24 object-contain border rounded-md mt-2"
                    />
                  )}
                </div>

                {/* <div className="space-y-2">
                  <Label htmlFor="upload_signature">Upload Signature</Label>
                  <Input
                    id="SignaturePath"
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;

                      const reader = new FileReader();
                      reader.onload = () => {
                        setSignatureImageSrc(reader.result);
                        setShowSignatureCropModal(true); // 🔥 open modal
                      };
                      reader.readAsDataURL(file);
                    }}
                  />
                  <p className="text-xs text-gray-500">
                    Allowed formats: JPG, JPEG, PNG
                  </p>
                  {(signaturePreview || employeeData.signaturePath) && (
                    <img
                      src={signaturePreview || ""}
                      alt="Signature Preview"
                      className="h-20 object-contain border rounded-md mt-2"
                    />
                  )}
                </div> */}

                <div className="space-y-2">
                  <Label htmlFor="upload_signature">Upload Signature</Label>

                  <div className="relative group">
                    <Input
                      id="SignaturePath"
                      type="file"
                      accept="image/*"
                      disabled={!employeeData.saveinfo}
                      className={!employeeData.saveinfo ? "cursor-not-allowed opacity-60" : ""}
                      onChange={(e) => {
                        if (!employeeData.saveinfo) return;

                        const file = e.target.files?.[0];
                        if (!file) return;

                        // Client-side size guard — backend enforces the same 2 MB cap.
                        const SIGNATURE_MAX_BYTES = 2 * 1024 * 1024;
                        if (file.size > SIGNATURE_MAX_BYTES) {
                          toast({ description: "Signature must be 2 MB or smaller.", variant: "destructive" });
                          e.target.value = "";
                          return;
                        }

                        const reader = new FileReader();

                        reader.onload = () => {
                          setSignatureImageSrc(reader.result);
                          setShowSignatureCropModal(true);
                        };

                        reader.readAsDataURL(file);
                      }}
                    />

                    {!employeeData.saveinfo && (
                      <>
                        <div className="absolute inset-0 z-10 cursor-not-allowed" />

                        <div className="absolute hidden group-hover:flex -top-10 left-0 bg-black text-white text-xs px-3 py-2 rounded-md shadow-lg z-20 whitespace-nowrap">
                          First save company information
                        </div>
                      </>
                    )}
                  </div>

                  <p className="text-xs text-gray-500">
                    Allowed formats: JPG, JPEG, PNG · Max size: 2 MB
                  </p>

                  {(signaturePreview || employeeData.signaturePath) && (
                    <img
                      src={signaturePreview || ""}
                      alt="Signature Preview"
                      className="h-20 object-contain border rounded-md mt-2"
                    />
                  )}
                </div>
              </div>

              <div className="flex gap-2 pt-4 justify-end">
                <Button type="submit" disabled={loading} style={{ backgroundColor: 'lightseagreen' }}>
                  {loading ? "Saving..." : "Save Company Information"}
                </Button>
              </div>
            </form>
          )}

          {/* Compliance & Salary Settings Tab */}
          {activeTab === "compliance" && (
            <div className="space-y-6">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h3 className="font-semibold text-blue-800 flex items-center gap-2">
                  <Shield className="h-5 w-5" />
                  Statutory Compliance Settings
                </h3>
                <p className="text-sm text-blue-600 mt-1">
                  Configure company-wide deduction settings. These will apply to all employees by default.
                </p>
              </div>

              {/* EPF Settings */}
              <Card className="border shadow-sm">
                <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Briefcase className="h-5 w-5 text-blue-600" />
                    EPF (Employees' Provident Fund)
                  </CardTitle>
                  <CardDescription>Configure EPF deduction rates and limits</CardDescription>
                </CardHeader>
                <CardContent className="p-6 space-y-4">
                  <div className="flex items-center gap-2 mb-4">
                    <Checkbox
                      id="pfApplicable"
                      checked={employeeData.pfApplicable}
                      onCheckedChange={(checked) => handleInputChange("pfApplicable", checked === true)}
                    />
                    <Label htmlFor="pfApplicable" className="cursor-pointer">Enable EPF Deduction</Label>
                  </div>

                  {employeeData.pfApplicable && (
                    <div className="ml-6 space-y-4">
                      {/* PF-in-CTC toggle */}
                      <div className="flex items-start gap-2 rounded-lg border border-gray-200 bg-gray-50 p-3">
                        <Checkbox
                          id="pfFromCtc"
                          checked={employeeData.pfFromCtc}
                          onCheckedChange={(checked) => handleInputChange("pfFromCtc", checked === true)}
                          className="mt-0.5"
                        />
                        <div>
                          <Label htmlFor="pfFromCtc" className="cursor-pointer font-medium">
                            Include employer PF in CTC
                          </Label>
                          <p className="text-xs text-gray-500 mt-0.5">
                            When on, the employer's 12% PF is part of CTC (so it reduces take-home).
                            When off, it's paid over and above CTC. The employee's own PF is always
                            deducted from salary either way.
                          </p>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <Label className="text-xs font-semibold text-gray-500">PF Basis</Label>
                          <Select
                            value={employeeData.pfBasis}
                            onValueChange={(value: any) => handleInputChange("pfBasis", value)}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="statutory">Statutory (capped at ₹{pfWageCeiling().toLocaleString("en-IN")})</SelectItem>
                              <SelectItem value="actual">Actual Basic Salary</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-xs font-semibold text-gray-500">
                            Employee Contribution Rate (%) <span className="text-gray-400 font-normal">— min 12%</span>
                          </Label>
                          <Input
                            type="number"
                            step="0.1"
                            min={12}
                            value={employeeData.pfEmployeeRate}
                            onChange={(e) => handleInputChange("pfEmployeeRate", Number(e.target.value))}
                            onBlur={(e) => {
                              if (Number(e.target.value) < 12) handleInputChange("pfEmployeeRate", 12);
                            }}
                            className="mt-1"
                          />
                          {employeeData.pfEmployeeRate < 12 && (
                            <p className="text-xs text-red-500 mt-1">Employee PF can't be below 12%.</p>
                          )}
                        </div>
                        <div>
                          <Label className="text-xs font-semibold text-gray-500">
                            Employer Contribution Rate (%) <span className="text-gray-400 font-normal">— fixed</span>
                          </Label>
                          <Input
                            type="number"
                            step="0.1"
                            value={PF_EMPLOYER_RATE_PCT}
                            readOnly
                            disabled
                            className="mt-1"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* ESIC Settings */}
              <Card className="border shadow-sm">
                <CardHeader className="bg-gradient-to-r from-green-50 to-emerald-50">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Briefcase className="h-5 w-5 text-green-600" />
                    ESIC (Employees' State Insurance Corporation)
                  </CardTitle>
                  <CardDescription>Configure ESIC deduction rates and limits</CardDescription>
                </CardHeader>
                <CardContent className="p-6 space-y-4">
                  <div className="flex items-center gap-2 mb-4">
                    <Checkbox
                      id="esicApplicable"
                      checked={employeeData.esicApplicable}
                      onCheckedChange={(checked) => handleInputChange("esicApplicable", checked === true)}
                    />
                    <Label htmlFor="esicApplicable" className="cursor-pointer">Enable ESIC Deduction</Label>
                  </div>

                  {employeeData.esicApplicable && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 ml-6">
                      <div>
                        <Label className="text-xs font-semibold text-gray-500">ESIC Capping Limit (₹/month)</Label>
                        <Input
                          type="number"
                          value={ESIC_WAGE_CEILING}
                          readOnly
                          disabled
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-xs font-semibold text-gray-500">Employee Contribution Rate (%)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={ESIC_EMPLOYEE_RATE_PCT}
                          readOnly
                          disabled
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-xs font-semibold text-gray-500">Employer Contribution Rate (%)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={ESIC_EMPLOYER_RATE_PCT}
                          readOnly
                          disabled
                          className="mt-1"
                        />
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Professional Tax Settings */}
              <Card className="border shadow-sm">
                <CardHeader className="bg-gradient-to-r from-purple-50 to-pink-50">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <IndianRupee className="h-5 w-5 text-purple-600" />
                    Professional Tax (PT)
                  </CardTitle>
                  <CardDescription>Configure Professional Tax based on state</CardDescription>
                </CardHeader>
                <CardContent className="p-6 space-y-4">
                  <div className="flex items-center gap-2 mb-4">
                    <Checkbox
                      id="ptApplicable"
                      checked={employeeData.ptApplicable}
                      onCheckedChange={(checked) => handleInputChange("ptApplicable", checked === true)}
                    />
                    <Label htmlFor="ptApplicable" className="cursor-pointer">Enable Professional Tax</Label>
                  </div>

                  {employeeData.ptApplicable && (
                    <div className="ml-6 space-y-3">
                      <div>
                        <Label className="text-xs font-semibold text-gray-500">Select State</Label>
                        <Select
                          value={employeeData.ptState}
                          onValueChange={(value) => handleInputChange("ptState", value)}
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue placeholder="Select state for PT calculation" />
                          </SelectTrigger>
                          <SelectContent className="bg-white max-h-60 ">
                            {Object.entries(PT_STATES).map(([key, value]) => (
                              <SelectItem key={key} value={key}>{value.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <div className="text-xs font-semibold text-gray-600 mb-2">PT Slabs for {ptCfg.label}</div>
                        <div className="space-y-1 text-xs">
                          {ptCfg.slabs.map((slab, idx) => (
                            <div key={idx} className="flex justify-between">
                              <span>₹{slab[0].toLocaleString()} - {slab[1] === 99999999 ? 'Above' : `₹${slab[1].toLocaleString()}`}</span>
                              <span className="font-medium">₹{slab[2]}/month</span>
                            </div>
                          ))}
                          {ptCfg.note && <p className="text-amber-600 mt-1">{ptCfg.note}</p>}
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Labour Welfare Fund Settings */}
              <Card className="border shadow-sm">
                <CardHeader className="bg-gradient-to-r from-orange-50 to-amber-50">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <IndianRupee className="h-5 w-5 text-orange-600" />
                    Labour Welfare Fund (LWF)
                  </CardTitle>
                  <CardDescription>Configure Labour Welfare Fund based on state</CardDescription>
                </CardHeader>
                <CardContent className="p-6 space-y-4">
                  <div className="flex items-center gap-2 mb-4">
                    <Checkbox
                      id="lwfApplicable"
                      checked={employeeData.lwfApplicable}
                      onCheckedChange={(checked) => handleInputChange("lwfApplicable", checked === true)}
                    />
                    <Label htmlFor="lwfApplicable" className="cursor-pointer">Enable LWF Deduction</Label>
                  </div>

                  {employeeData.lwfApplicable && (
                    <div className="ml-6 space-y-3">
                      <div>
                        <Label className="text-xs font-semibold text-gray-500">Select State</Label>
                        <Select
                          value={employeeData.lwfState}
                          onValueChange={(value) => handleInputChange("lwfState", value)}
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue placeholder="Select state for LWF calculation" />
                          </SelectTrigger>
                          <SelectContent className="bg-white max-h-60">
                            {Object.entries(LWF_STATES).map(([key, value]) => (
                              <SelectItem key={key} value={key}>{value.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <div className="text-xs font-semibold text-gray-600 mb-2">LWF Rates for {lwfCfg.label}</div>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <div>Employee Contribution: <span className="font-bold">₹{lwfCfg.ee}</span></div>
                          <div>Employer Contribution: <span className="font-bold">₹{lwfCfg.er}</span></div>
                          <div>Frequency: <span className="font-bold capitalize">{lwfCfg.freq}</span></div>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Tax Regime Settings */}
              <Card className="border shadow-sm">
                <CardHeader className="bg-gradient-to-r from-red-50 to-rose-50">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Shield className="h-5 w-5 text-red-600" />
                    Income Tax (TDS) Settings
                  </CardTitle>
                  <CardDescription>Configure default tax regime and deduction limits</CardDescription>
                </CardHeader>
                <CardContent className="p-6 space-y-4">
                  <div className="flex items-center gap-2 mb-4">
                    <Checkbox
                      id="tdsApplicable"
                      checked={employeeData.tdsApplicable}
                      onCheckedChange={(checked) => handleInputChange("tdsApplicable", checked === true)}
                    />
                    <Label htmlFor="tdsApplicable" className="cursor-pointer">Enable TDS Deduction</Label>
                  </div>

                  {employeeData.tdsApplicable && (
                    <div className="ml-6 space-y-4">
                      <div>
                        <Label className="text-xs font-semibold text-gray-500">Default Tax Regime</Label>
                        <Select
                          value={employeeData.defaultTaxRegime}
                          onValueChange={(value: any) => handleInputChange("defaultTaxRegime", value)}
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="new">New Regime (Default - ₹{STD_DEDUCTION_NEW.toLocaleString("en-IN")} Standard Deduction)</SelectItem>
                            <SelectItem value="old">Old Regime (With 80C, HRA, etc.)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {/* When TDS is enabled, the statutory amounts are fixed
                          (FY 2025-26 values) and shown as read-only so the
                          user can see them but can't tamper with them. */}
                      <p className="text-[11px] text-gray-500 italic ml-1">
                        These are statutory figures for FY 2025-26, shown for reference —
                        they come from the payroll calculation itself and are not saved as
                        company settings.
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="bg-blue-50 p-3 rounded-lg">
                          <h4 className="font-semibold text-blue-800 text-sm mb-2">New Regime Settings</h4>
                          <div className="space-y-2 text-sm">
                            <div>
                              <Label className="text-xs">Standard Deduction (₹/year)</Label>
                              <Input
                                type="number"
                                value={STD_DEDUCTION_NEW}
                                className="mt-1 bg-gray-100 cursor-not-allowed"
                                readOnly
                                tabIndex={-1}
                              />
                            </div>
                            <div>
                              <Label className="text-xs">Section 87A Rebate (₹)</Label>
                              <Input
                                type="number"
                                value={REBATE_87A_NEW}
                                className="mt-1 bg-gray-100 cursor-not-allowed"
                                readOnly
                                tabIndex={-1}
                              />
                            </div>
                            <div>
                              <Label className="text-xs">Rebate Applicable up to (₹)</Label>
                              <Input
                                type="number"
                                value={REBATE_87A_LIMIT_NEW}
                                className="mt-1 bg-gray-100 cursor-not-allowed"
                                readOnly
                                tabIndex={-1}
                              />
                            </div>
                          </div>
                        </div>

                        <div className="bg-amber-50 p-3 rounded-lg">
                          <h4 className="font-semibold text-amber-800 text-sm mb-2">Old Regime Settings</h4>
                          <div className="space-y-2 text-sm">
                            <div>
                              <Label className="text-xs">Standard Deduction (₹/year)</Label>
                              <Input
                                type="number"
                                value={STD_DEDUCTION_OLD}
                                className="mt-1 bg-gray-100 cursor-not-allowed"
                                readOnly
                                tabIndex={-1}
                              />
                            </div>
                            <div>
                              <Label className="text-xs">Section 87A Rebate (₹)</Label>
                              <Input
                                type="number"
                                value={REBATE_87A_OLD}
                                className="mt-1 bg-gray-100 cursor-not-allowed"
                                readOnly
                                tabIndex={-1}
                              />
                            </div>
                            <div>
                              <Label className="text-xs">Rebate Applicable up to (₹)</Label>
                              <Input
                                type="number"
                                value={REBATE_87A_LIMIT_OLD}
                                className="mt-1 bg-gray-100 cursor-not-allowed"
                                readOnly
                                tabIndex={-1}
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="bg-gray-50 p-3 rounded-lg">
                        <h4 className="font-semibold text-gray-700 text-sm mb-2">Tax Slabs (FY 2025-26)</h4>
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          <div>
                            <p className="font-medium">New Regime Slabs:</p>
                            <ul className="list-disc list-inside space-y-1 mt-1">
                              <li>₹0 - ₹4,00,000: 0%</li>
                              <li>₹4,00,001 - ₹8,00,000: 5%</li>
                              <li>₹8,00,001 - ₹12,00,000: 10%</li>
                              <li>₹12,00,001 - ₹16,00,000: 15%</li>
                              <li>₹16,00,001 - ₹20,00,000: 20%</li>
                              <li>₹20,00,001 - ₹24,00,000: 25%</li>
                              <li>Above ₹24,00,000: 30%</li>
                            </ul>
                          </div>
                          <div>
                            <p className="font-medium">Old Regime Slabs:</p>
                            <ul className="list-disc list-inside space-y-1 mt-1">
                              <li>₹0 - ₹2,50,000: 0%</li>
                              <li>₹2,50,001 - ₹5,00,000: 5%</li>
                              <li>₹5,00,001 - ₹10,00,000: 20%</li>
                              <li>Above ₹10,00,000: 30%</li>
                            </ul>
                          </div>
                        </div>
                        <p className="text-xs text-gray-500 mt-2">* Plus 4% Health & Education Cess</p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              <div className="flex gap-2 pt-4 justify-end">
                <Button
                  onClick={handleComplianceSubmit}
                  disabled={loading}
                  style={{ backgroundColor: 'lightseagreen' }}
                >
                  {loading ? "Saving..." : "Save Compliance Settings"}
                </Button>
              </div>
            </div>
          )}

          {/* Leaves Settings Tab — includes a "Leave Policy" button in its
              own header that opens the deductions / working-days / overtime
              dialog. */}
          {activeTab === "leaves" && <LeaveSettings />}
        </CardContent>
      </Card>
      {showCropModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">

          <div className="bg-white p-6 rounded-xl w-[700px]">

            {/* Crop Area */}
            <div className="flex gap-6">

              {/* Left: Cropper */}
              <div className="w-2/3">
                <ReactCrop
                  crop={crop}
                  onChange={(c) => setCrop(c)}
                  onComplete={(c) => setCompletedCrop(c)}
                >
                  <img
                    ref={imgRef}
                    src={imageSrc}
                    alt="Crop"
                  // style={{
                  //   transform: `scale(${zoom}) rotate(${rotation}deg)`
                  // }}
                  />
                </ReactCrop>
              </div>

              {/* Right: Preview */}
              <div className="w-1/3">
                <p className="text-sm mb-2 font-semibold">Preview</p>
                <canvas id="previewCanvas" className="border w-full" />
              </div>
            </div>

            {/* Controls */}
            <div className="mt-4 space-y-3">

              {/* Zoom */}
              <div>
                <label className="text-sm">Zoom</label>
                <input
                  type="range"
                  min="1"
                  max="3"
                  step="0.1"
                  value={zoom}
                  onChange={(e) => setZoom(e.target.value)}
                  className="w-full"
                />
              </div>

              {/* Rotation */}
              <div>
                <label className="text-sm">Rotate</label>
                <input
                  type="range"
                  min="0"
                  max="360"
                  value={rotation}
                  onChange={(e) => setRotation(e.target.value)}
                  className="w-full"
                />
              </div>

            </div>

            {/* Buttons */}
            <div className="flex justify-end gap-2 mt-4">
              <button
                className="px-4 py-2 border rounded"
                onClick={() => setShowCropModal(false)}
              >
                Cancel
              </button>

              <button
                className="bg-blue-600 text-white px-4 py-2 rounded"
                onClick={handleCrop}
              >
                Crop & Save
              </button>
            </div>

          </div>
        </div>
      )}



      {showSignatureCropModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">

          <div className="bg-white p-6 rounded-xl w-[700px]">

            {/* Crop Area */}
            <div className="flex gap-6">

              {/* Left: Cropper */}
              <div className="w-2/3">
                <ReactCrop
                  crop={signatureCrop}
                  onChange={(c) => setSignatureCrop(c)}
                  onComplete={(c) => setCompletedSignatureCrop(c)}
                >
                  <img
                    ref={signatureImgRef}
                    src={signatureImageSrc}
                    alt="Signature"
                  // style={{
                  //   transform: `scale(${signatureZoom}) rotate(${signatureRotation}deg)`
                  // }}
                  />
                </ReactCrop>
              </div>

              {/* Right: Preview */}
              <div className="w-1/3">
                <p className="text-sm mb-2 font-semibold">Preview</p>
                <canvas id="signaturePreviewCanvas" className="border w-full" />
              </div>
            </div>

            {/* Controls */}
            <div className="mt-4 space-y-3">

              <div>
                <label>Zoom</label>
                <input
                  type="range"
                  min="1"
                  max="3"
                  step="0.1"
                  value={signatureZoom}
                  onChange={(e) => setSignatureZoom(Number(e.target.value))}
                  className="w-full"
                />
              </div>

              <div>
                <label>Rotate</label>
                <input
                  type="range"
                  min="0"
                  max="360"
                  value={signatureRotation}
                  onChange={(e) => setSignatureRotation(Number(e.target.value))}
                  className="w-full"
                />
              </div>

            </div>

            {/* Buttons */}
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setShowSignatureCropModal(false)}>
                Cancel
              </button>

              <button
                className="bg-blue-600 text-white px-4 py-2 rounded"
                onClick={handleSignatureCrop}
              >
                Crop & Save
              </button>
            </div>

          </div>
        </div>
      )}
    </div>
  );
};


export default AddCompany;